import { motion } from "framer-motion";
import { Phone, ArrowRight, MapPin, Star } from "lucide-react";

export default function Hero({ content }) {
  const tel = content.phone.replace(/[^+\d]/g, "");

  return (
    <section id="top" className="relative pt-32 pb-20 md:pt-40 md:pb-28 overflow-hidden bg-neutral-950 text-white" data-testid="hero-section">
      {/* Background image */}
      {content.hero_background && (
        <div className="absolute inset-0">
          <img
            src={content.hero_background}
            alt="Construction background"
            className="w-full h-full object-cover opacity-40"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-neutral-950/70 via-neutral-950/40 to-neutral-950" />
        </div>
      )}
      {/* Glow */}
      <div className="absolute inset-0 hero-glow pointer-events-none" />
      <div className="relative grain" />

      <div className="relative max-w-[1400px] mx-auto px-6 lg:px-10">
        <div className="grid lg:grid-cols-12 gap-10 items-center">
          {/* Left content */}
          <div className="lg:col-span-7">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="inline-flex items-center gap-2 border border-orange-500/40 bg-orange-500/10 px-3 py-1.5 mb-8"
              data-testid="hero-badge"
            >
              <MapPin className="w-3.5 h-3.5 text-orange-400" />
              <span className="text-xs uppercase tracking-widest text-orange-200 font-medium">
                {content.location}
              </span>
            </motion.div>

            <motion.h1
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.1 }}
              className="font-display font-black uppercase leading-[0.9] tracking-tighter text-5xl sm:text-6xl md:text-7xl lg:text-[6.5rem]"
              data-testid="hero-headline"
            >
              {content.hero_headline.split(/\.(\s+|$)/).filter((s) => s && s.trim()).map((line, i, arr) => (
                <span key={i} className="block">
                  {line.trim()}
                  {i < arr.length - 1 && <span className="text-orange-500">.</span>}
                </span>
              ))}
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.25 }}
              className="mt-8 text-lg md:text-xl text-neutral-300 max-w-2xl leading-relaxed"
              data-testid="hero-subheadline"
            >
              {content.hero_subheadline}
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.4 }}
              className="mt-10 flex flex-col sm:flex-row items-start sm:items-center gap-4"
            >
              <a
                href={`tel:${tel}`}
                className="group inline-flex items-center gap-3 bg-orange-600 hover:bg-orange-700 text-white font-display font-bold uppercase tracking-wider px-8 py-5 text-base transition-colors shadow-[8px_8px_0_rgba(234,88,12,0.35)] hover:-translate-y-1 duration-200"
                data-testid="hero-call-btn"
              >
                <Phone className="w-5 h-5" />
                Call {content.phone}
                <ArrowRight className="w-5 h-5 transition-transform group-hover:translate-x-1" />
              </a>
              <a
                href="#contact"
                className="inline-flex items-center gap-3 border border-white/30 hover:border-orange-500 hover:bg-orange-500/10 text-white font-display font-bold uppercase tracking-wider px-8 py-5 text-base transition-colors"
                data-testid="hero-quote-btn"
              >
                Free Estimate
              </a>
            </motion.div>

            {/* Trust row */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.7, delay: 0.6 }}
              className="mt-12 flex flex-wrap items-center gap-8"
            >
              <div className="flex items-center gap-2">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className="w-4 h-4 fill-orange-400 text-orange-400" />
                ))}
                <span className="text-sm text-neutral-300 ml-1">5.0 rated</span>
              </div>
              <div className="flex items-center gap-6 text-sm text-neutral-400">
                <span><strong className="text-white font-display text-lg">{content.years_experience}+</strong> Years</span>
                <span className="w-px h-6 bg-neutral-700" />
                <span><strong className="text-white font-display text-lg">{content.projects_completed}+</strong> Projects</span>
                <span className="w-px h-6 bg-neutral-700" />
                <span>Licensed & Insured</span>
              </div>
            </motion.div>
          </div>

          {/* Right side — Dynamic Logo */}
          <div className="lg:col-span-5 relative flex items-center justify-center">
            <motion.div
              initial={{ opacity: 0, scale: 0.85, rotate: -6 }}
              animate={{ opacity: 1, scale: 1, rotate: 0 }}
              transition={{ duration: 1, ease: "easeOut", delay: 0.3 }}
              className="relative"
            >
              {/* Floating framer motion loop */}
              <motion.div
                animate={{ y: [0, -14, 0] }}
                transition={{ duration: 5, repeat: Infinity, ease: "easeInOut" }}
                className="relative"
              >
                {/* Halo */}
                <div className="absolute -inset-8 bg-orange-500/25 blur-3xl rounded-full" />
                <div className="absolute -inset-4 bg-white/5 backdrop-blur-md border border-white/10" />
                {content.logo_url && (
                  <img
                    src={content.logo_url}
                    alt={`${content.business_name} logo`}
                    className="relative w-full max-w-md mx-auto object-contain drop-shadow-[0_20px_50px_rgba(234,88,12,0.4)]"
                    data-testid="hero-logo"
                  />
                )}
              </motion.div>

              {/* Rotating ring */}
              <motion.div
                animate={{ rotate: 360 }}
                transition={{ duration: 40, repeat: Infinity, ease: "linear" }}
                className="absolute -inset-6 border border-dashed border-orange-500/40 rounded-full pointer-events-none"
              />
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
}
